import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { getUsers, createUser, updateUser, deleteUser, getMembers, approveUser } from '../../services/api';
import { FiPlus, FiEdit2, FiTrash2, FiX, FiUsers, FiSearch } from 'react-icons/fi';
import Toast from '../../components/Toast';
import { useAuth } from '../../context/AuthContext';

// Autocomplete member search - filters out already linked members
const MemberSearch = ({ members, linkedMemberIds, value, onChange }) => {
  const [query, setQuery] = useState('');
  const [show, setShow] = useState(false);
  const [fathersOnly, setFathersOnly] = useState(false);
  const [dropPos, setDropPos] = useState({ top: 0, left: 0, width: 0 });
  const inputRef = useRef();

  const selected = members.find(m => String(m.id) === String(value));
  const linkedSet = new Set(linkedMemberIds.map(String));
  // Tokenised match — "DES H" finds "DESSALE HABTOM" (each token must
  // appear somewhere across first/middle/last/phone/card/christian_name).
  // Same pattern as the All Members search + CR Paid From.
  const q = query.trim().toLowerCase();
  const tokens = q ? q.split(/\s+/).filter(Boolean) : [];
  const matchesQuery = (m) => {
    if (!tokens.length) return true;
    const hay = [m.first_name, m.middle_name, m.last_name, m.phone, m.card_number, m.christian_name]
      .filter(Boolean).join(' ').toLowerCase();
    return tokens.every(t => hay.includes(t));
  };
  const passesFather = (m) => !fathersOnly || m.is_spiritual_father === 1;
  // Show every match, including members already linked to another user.
  // The list carries an "also linked" badge so the operator knows —
  // saving is not blocked (per admin policy: multiple staff accounts
  // may share one member record when the person wears many hats).
  const filtered = members
    .filter(m => passesFather(m) && matchesQuery(m))
    .slice(0, 12);

  const openDrop = () => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropPos({ top: rect.bottom + window.scrollY + 4, left: rect.left + window.scrollX, width: rect.width });
    }
    setShow(true);
  };

  const select = (m) => { onChange(m.id); setQuery(''); setShow(false); };
  const clear = () => { onChange(''); setQuery(''); };

  return (
    <div ref={inputRef}>
      {selected ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', background: '#eef2ff', border: '1px solid #a5b4fc', borderRadius: 8 }}>
          <span style={{ fontWeight: 600, color: '#4f46e5', flex: 1 }}>
            {selected.first_name} {selected.middle_name || ''} {selected.last_name}
          </span>
          <button type="button" className="btn-icon" onClick={clear}><FiX /></button>
        </div>
      ) : (
        <div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#6b7280', marginBottom: 6, cursor: 'pointer' }}>
            <input type="checkbox" checked={fathersOnly} onChange={e => { setFathersOnly(e.target.checked); openDrop(); }} style={{ width: 'auto', accentColor: '#7c3aed' }} />
            Spiritual fathers only
          </label>
          <div className="search-input-container" style={{ margin: 0 }}>
            <FiSearch style={{ color: '#9ca3af' }} />
            <input
              type="text"
              placeholder={fathersOnly ? 'Search spiritual fathers…' : 'Search member by name or phone...'}
              value={query}
              onChange={e => { setQuery(e.target.value); openDrop(); }}
              onFocus={openDrop}
              onBlur={() => setTimeout(() => setShow(false), 150)}
              autoComplete="off"
            />
          </div>
        </div>
      )}
      {show && !selected && filtered.length > 0 && (
        <div style={{
          position: 'fixed', top: dropPos.top, left: dropPos.left, width: dropPos.width,
          zIndex: 99999, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8,
          boxShadow: '0 8px 24px rgba(0,0,0,0.15)', maxHeight: 300, overflowY: 'auto'
        }}>
          {filtered.map(m => {
            const alreadyLinked = linkedSet.has(String(m.id));
            return (
              <div key={m.id} onMouseDown={() => select(m)}
                style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6' }}
                onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <div style={{ fontWeight: 600, fontSize: 13 }}>
                  {m.first_name} {m.middle_name || ''} {m.last_name}
                  {m.is_spiritual_father === 1 && <span className="pill pill-purple" style={{ marginLeft: 6, fontSize: 10 }}>Father</span>}
                  {alreadyLinked && <span className="pill pill-yellow" style={{ marginLeft: 6, fontSize: 10 }}>Also linked</span>}
                </div>
                <div style={{ fontSize: 11, color: '#9ca3af' }}>{m.phone || ''}</div>
              </div>
            );
          })}
        </div>
      )}
      {show && !selected && query && filtered.length === 0 && (
        <div style={{
          position: 'fixed', top: dropPos.top, left: dropPos.left, width: dropPos.width,
          zIndex: 99999, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8,
          padding: '12px 14px', fontSize: 13, color: '#9ca3af',
          boxShadow: '0 8px 24px rgba(0,0,0,0.15)'
        }}>
          No members match "{query}"
        </div>
      )}
    </div>
  );
};

const rolePill = { admin: 'pill-purple', pastor: 'pill-blue', finance: 'pill-green', cashier: 'pill-blue', secretary: 'pill-yellow', cell_leader: 'pill-gray', member: 'pill-gray' };
const STAFF_ROLES = ['admin', 'pastor', 'finance', 'cashier', 'secretary', 'cell_leader'];

const blank = { name: '', username: '', email: '', password: '', role: 'secretary', active: true, member_id: '' };

const UsersRoles = () => {
  const { t } = useTranslation();
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [members, setMembers] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3000); };
  const set = f => e => setForm(p => ({ ...p, [f]: f === 'active' ? e.target.checked : e.target.value }));

  const [approveModal, setApproveModal] = useState(null);
  const [approveForm, setApproveForm] = useState({ role: 'member', member_id: '' });
  const [tab, setTab] = useState('staff'); // staff | members | spiritualFathers
  // v1.9.113 — search + sort for the users table (client-side, like All Members).
  const [userSearch, setUserSearch] = useState('');
  const [userSort, setUserSort] = useState('name');

  const fetch = () => getUsers().then(r => setUsers(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  useEffect(() => {
    fetch();
    // include_sub=1 so dependents / spouses / children (non-head-of-household)
    // are also linkable, otherwise the picker silently hides them.
    getMembers({ include_sub: '1' }).then(r => setMembers(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  const handleAdd = async () => {
    setError('');
    if (!form.name || !form.username || !form.password) return setError('Name, username, and password are required');
    if (!/^\d{4,}$/.test(form.password)) return setError('Password must be at least 4 digits and contain only numbers');
    setSaving(true);
    try {
      await createUser(form);
      setShowAdd(false);
      setForm(blank);
      showToast('User created');
      fetch();
    } catch (e) { setError(e.response?.data?.error || 'Failed'); }
    finally { setSaving(false); }
  };

  const handleEdit = async () => {
    setError('');
    if (!editUser.name || !editUser.email) return setError('Name and email are required');
    if (editUser.password && !/^\d{4,}$/.test(editUser.password)) return setError('Password must be at least 4 digits and contain only numbers');
    setSaving(true);
    try {
      await updateUser(editUser.id, { ...editUser, username: editUser.email });
      setEditUser(null);
      showToast('User updated');
      fetch();
    } catch (e) { setError(e.response?.data?.error || 'Failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Delete user ${u.name}?`)) return;
    try {
      await deleteUser(u.id);
      showToast('User deleted', 'error');
      fetch();
    } catch (e) { showToast(e.response?.data?.error || 'Failed', 'error'); }
  };

  const setEdit = f => e => setEditUser(p => ({ ...p, [f]: f === 'active' ? e.target.checked : e.target.value }));

  return (
    <div className="page-content">
      <div className="page-header">
        <div><h1>{t('nav.usersRoles')}</h1><p>{users.length}</p></div>
        <button className="btn btn-primary" onClick={() => { setShowAdd(true); setForm(blank); setError(''); }}><FiPlus /> {t('common.add')}</button>
      </div>

      <div className="card" style={{ marginBottom: 16, padding: '12px 16px', fontSize: 13, color: '#6b7280', background: '#fffbeb', border: '1px solid #fde68a' }}>
        <strong>Roles:</strong> Admin (full access) · Pastor · Finance · Cashier · Secretary · Cell Leader · Member (portal only)
      </div>

      {/* Pending approval accounts */}
      {users.filter(u => !u.active && u.self_registered && !u.member_id).length > 0 && (
        <div className="card" style={{ marginBottom: 16, border: '1px solid #fde68a', background: '#fffbeb' }}>
          <div className="card-header" style={{ color: '#92400e', display: 'flex', alignItems: 'center', gap: 8 }}>
            ⏳ {t('usersRoles.pendingApproval')} ({users.filter(u => !u.active && u.self_registered && !u.member_id).length})
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table>
              <thead><tr><th>{t('common.name')}</th><th>{t('auth.username')}</th><th>{t('common.phone')}</th><th>{t('usersRoles.requestType')}</th><th>{t('members.registeredOn')}</th><th>{t('common.actions')}</th></tr></thead>
              <tbody>
                {users.filter(u => !u.active && u.self_registered && !u.member_id).map(u => (
                  <tr key={u.id} style={{ background: '#fffbeb' }}>
                    <td><strong>{u.name}</strong></td>
                    <td><span style={{ fontFamily: 'monospace', color: '#4f46e5' }}>{u.email || '—'}</span></td>
                    <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{u.phone || '—'}</td>
                    <td>
                      {u.registration_type === 'existing' ? (
                        <div>
                          <span className="pill pill-blue">{t('usersRoles.existingMember')}</span>
                          {u.requested_member_name && (
                            <div style={{ fontSize: 11, color: '#374151', marginTop: 3 }}>
                              → {u.requested_member_name.replace(/\s+/g, ' ').trim()}
                              {u.requested_member_card && <span style={{ fontFamily: 'monospace', color: '#6b7280', marginLeft: 4 }}>{u.requested_member_card}</span>}
                            </div>
                          )}
                        </div>
                      ) : u.registration_type === 'new' ? (
                        <span className="pill pill-purple">{t('usersRoles.newMember')}</span>
                      ) : (
                        <span style={{ color: '#9ca3af', fontSize: 12 }}>—</span>
                      )}
                    </td>
                    <td style={{ fontSize: 12, color: '#6b7280' }}>{u.created_at?.slice(0, 16)}</td>
                    <td>
                      <div className="action-btns">
                        <button className="btn btn-sm btn-success" onClick={() => { setApproveModal(u); setApproveForm({ role: 'member', member_id: u.requested_member_id || '', spiritual_father_id: '' }); }}>✓ {t('approve.approveBtn')}</button>
                        <button className="btn-icon danger" onClick={() => handleDelete(u)}><FiTrash2 /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {(() => {
        // Tokenised search: every word must appear in name / username / phone /
        // linked member / card / role, so "SIR ADM" finds Sirak (admin).
        const tokens = userSearch.trim().toLowerCase().split(/s+/).filter(Boolean);
        const matches = (u) => {
          if (!tokens.length) return true;
          const hay = [u.name, u.email, u.phone, u.linked_member_name, u.linked_member_card, u.role]
            .filter(Boolean).join(' ').toLowerCase();
          return tokens.every(tk => hay.includes(tk));
        };
        const byName = (a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });
        const SORTS = {
          name: byName,
          name_desc: (a, b) => byName(b, a),
          username: (a, b) => (a.email || '').localeCompare(b.email || '', undefined, { sensitivity: 'base' }),
          role: (a, b) => (a.role || '').localeCompare(b.role || '') || byName(a, b),
          newest: (a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')),
          oldest: (a, b) => String(a.created_at || '').localeCompare(String(b.created_at || '')),
          // Linked member A→Z; users with no linked member go to the bottom.
          linked: (a, b) => {
            const la = (a.linked_member_name || '').replace(/s+/g, ' ').trim();
            const lb = (b.linked_member_name || '').replace(/s+/g, ' ').trim();
            if (!la !== !lb) return la ? -1 : 1;
            return la.localeCompare(lb, undefined, { sensitivity: 'base' }) || byName(a, b);
          },
          active: (a, b) => (b.active ? 1 : 0) - (a.active ? 1 : 0) || byName(a, b),
        };
        const visible = users
          .filter(u => (u.active || !u.self_registered || u.member_id) && matches(u))
          .sort(SORTS[userSort] || byName);
        const isFather = (u) => {
          if (!u.member_id) return false;
          const m = members.find(mm => mm.id === u.member_id || String(mm.id) === String(u.member_id));
          return m && m.is_spiritual_father === 1;
        };
        const staffUsers = visible.filter(u => STAFF_ROLES.includes(u.role));
        const memberUsers = visible.filter(u => u.role === 'member' && !isFather(u));
        const fatherUsers = visible.filter(u => u.role === 'member' && isFather(u));
        const tabRows = tab === 'staff' ? staffUsers : tab === 'members' ? memberUsers : fatherUsers;
        const TABS = [
          { key: 'staff', label: 'Staff', count: staffUsers.length },
          { key: 'members', label: 'Members', count: memberUsers.length },
          { key: 'spiritualFathers', label: 'Spiritual Fathers', count: fatherUsers.length },
        ];
        return (
          <>
            <div style={{ display: 'flex', borderBottom: '2px solid #e5e7eb', marginBottom: 14, overflowX: 'auto' }}>
              {TABS.map(tb => (
                <button key={tb.key} onClick={() => setTab(tb.key)} style={{
                  padding: '10px 18px', border: 'none', background: 'none', cursor: 'pointer',
                  fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap',
                  color: tab === tb.key ? '#4f46e5' : '#6b7280',
                  borderBottom: tab === tb.key ? '2px solid #4f46e5' : '2px solid transparent', marginBottom: -2,
                }}>
                  {tb.label}
                  <span style={{ marginLeft: 6, background: tab === tb.key ? '#4f46e5' : '#e5e7eb', color: tab === tb.key ? '#fff' : '#6b7280', borderRadius: 10, padding: '1px 8px', fontSize: 11 }}>{tb.count}</span>
                </button>
              ))}
            </div>
            <div className="filters-row">
              <div className="search-input-container" style={{ margin: 0, flex: 1, maxWidth: 300 }}>
                <FiSearch style={{ color: '#9ca3af' }} />
                <input placeholder="Search by name, username, phone or role…" value={userSearch} onChange={e => setUserSearch(e.target.value)} />
                {userSearch && (
                  <button onClick={() => setUserSearch('')} aria-label="Clear search" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', display: 'flex', padding: 2 }}><FiX /></button>
                )}
              </div>
              <select className="filter-select" value={userSort} onChange={e => setUserSort(e.target.value)}>
                <option value="name">Sort: Name (A→Z)</option>
                <option value="name_desc">Sort: Name (Z→A)</option>
                <option value="username">Sort: Username</option>
                <option value="role">Sort: Role</option>
                <option value="linked">Sort: Linked Member</option>
                <option value="active">Sort: Active first</option>
                <option value="newest">Sort: Newest first</option>
                <option value="oldest">Sort: Oldest first</option>
              </select>
            </div>
            <div className="table-card">
              <table>
                <thead>
                  <tr><th>#</th><th>{t('common.name')}</th><th>{t('auth.username')}</th><th>Linked Member</th><th>Role</th><th>{t('common.status')}</th><th>{t('common.actions')}</th></tr>
                </thead>
                <tbody>
                  {tabRows.length === 0 ? (
                    <tr><td colSpan={7}><div className="empty-state"><div className="empty-icon"><FiUsers /></div><p>{t('finance.noRecords')}</p></div></td></tr>
                  ) : tabRows.map((u, i) => (
                    <tr key={u.id}>
                      <td data-label="#">{i + 1}</td>
                      <td data-label={t('common.name')}>
                        <strong>{u.name}</strong>
                        {u.id === me?.id && <span className="pill pill-blue" style={{ marginLeft: 8, fontSize: 10 }}>You</span>}
                        {tab === 'spiritualFathers' && <span className="pill pill-purple" style={{ marginLeft: 6, fontSize: 10 }}>Father</span>}
                      </td>
                      <td data-label={t('auth.username')}><span style={{ fontFamily: 'monospace', color: '#4f46e5' }}>{u.email || '—'}</span></td>
                      <td data-label="Linked Member">
                        {u.linked_member_name ? (
                          <span style={{ fontSize: 13 }}>
                            {u.linked_member_name.replace(/\s+/g, ' ').trim()}
                            {u.linked_member_card && <span style={{ color: '#9ca3af', fontSize: 11, marginLeft: 6 }}>{u.linked_member_card}</span>}
                            {u.linked_is_father === 1 && <span className="pill pill-purple" style={{ marginLeft: 6, fontSize: 10 }}>Father</span>}
                          </span>
                        ) : <span style={{ color: '#9ca3af' }}>—</span>}
                      </td>
                      <td data-label="Role"><span className={`pill ${rolePill[u.role]}`}>{u.role}</span></td>
                      <td data-label={t('common.status')}><span className={`pill ${u.active ? 'pill-green' : 'pill-gray'}`}>{u.active ? t('common.active') : t('common.inactive')}</span></td>
                      <td data-label={t('common.actions')}>
                        <div className="action-btns">
                          <button className="btn-icon" onClick={() => { setEditUser({ ...u, password: '' }); setError(''); }}><FiEdit2 /></button>
                          {u.id !== me?.id && <button className="btn-icon danger" onClick={() => handleDelete(u)}><FiTrash2 /></button>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        );
      })()}

      {/* Add Modal */}
      {showAdd && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 480 }}>
            <div className="modal-header">
              <div><h3>Add User</h3></div>
              <button className="btn-icon" onClick={() => setShowAdd(false)}><FiX /></button>
            </div>
            <div className="modal-body">
              {error && <div className="error-msg">{error}</div>}
              <div className="form-grid">
                <div className="form-group"><label>Full Name <span className="required">*</span></label><input value={form.name} onChange={set('name')} /></div>
                <div className="form-group"><label>Username <span className="required">*</span></label><input value={form.username} onChange={set('username')} placeholder="e.g. sirak_solomon" /></div>
                <div className="form-group"><label>Password <span className="required">*</span> <span style={{ fontWeight: 400, fontSize: 11, color: '#9ca3af' }}>(min 4 digits, numbers only)</span></label><input type="password" inputMode="numeric" pattern="[0-9]*" maxLength={20} value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value.replace(/\D/g, '') }))} /></div>
                <div className="form-group"><label>Role</label>
                  <select value={form.role} onChange={set('role')}>
                    <option value="admin">Admin</option>
                    <option value="pastor">Pastor</option>
                    <option value="finance">Finance</option>
                    <option value="cashier">Cashier</option>
                    <option value="secretary">Secretary</option>
                    <option value="cell_leader">Cell Leader</option>
                    <option value="member">Member (Portal Access)</option>
                  </select>
                </div>
                <div className="form-group form-full">
                  <label>
                    Link to Member Record
                    <span style={{ fontWeight: 400, color: '#9ca3af', fontSize: 11, marginLeft: 8 }}>
                      {form.role === 'member' ? '(required — the member this account belongs to)' : '(optional — for staff who are also church members, e.g. priest-admin)'}
                    </span>
                  </label>
                  <MemberSearch members={members} linkedMemberIds={users.filter(u => u.member_id).map(u => u.member_id)} value={form.member_id} onChange={v => setForm(p => ({ ...p, member_id: v }))} />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowAdd(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleAdd} disabled={saving}>{saving ? 'Saving...' : 'Create User'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editUser && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 480 }}>
            <div className="modal-header">
              <div><h3>Edit User</h3><p>{editUser.name}</p></div>
              <button className="btn-icon" onClick={() => setEditUser(null)}><FiX /></button>
            </div>
            <div className="modal-body">
              {error && <div className="error-msg">{error}</div>}
              <div className="form-grid">
                <div className="form-group"><label>Full Name <span className="required">*</span></label><input value={editUser.name} onChange={setEdit('name')} /></div>
                <div className="form-group"><label>Username <span className="required">*</span></label><input type="text" value={editUser.email || ''} onChange={setEdit('email')} /></div>
                <div className="form-group"><label>New Password <span style={{ fontWeight: 400, color: '#9ca3af' }}>(min 4 digits, numbers only — leave blank to keep)</span></label><input type="password" inputMode="numeric" pattern="[0-9]*" maxLength={20} value={editUser.password || ''} onChange={e => setEditUser(p => ({ ...p, password: e.target.value.replace(/\D/g, '') }))} /></div>
                <div className="form-group"><label>Role</label>
                  <select value={editUser.role} onChange={setEdit('role')}>
                    <option value="admin">Admin</option>
                    <option value="pastor">Pastor</option>
                    <option value="finance">Finance</option>
                    <option value="cashier">Cashier</option>
                    <option value="secretary">Secretary</option>
                    <option value="cell_leader">Cell Leader</option>
                    <option value="member">Member (Portal Access)</option>
                  </select>
                </div>
                <div className="form-group form-full">
                  <label>
                    Link to Member Record
                    <span style={{ fontWeight: 400, color: '#9ca3af', fontSize: 11, marginLeft: 8 }}>
                      {editUser.role === 'member' ? '(required — the member this account belongs to)' : '(optional — for staff who are also church members, e.g. priest-admin)'}
                    </span>
                  </label>
                  <MemberSearch members={members} linkedMemberIds={users.filter(u => u.member_id && u.id !== editUser.id).map(u => u.member_id)} value={editUser.member_id || ''} onChange={v => setEditUser(p => ({ ...p, member_id: v }))} />
                </div>
                {(() => {
                  const linkedMember = editUser.member_id ? members.find(m => m.id === editUser.member_id || String(m.id) === String(editUser.member_id)) : null;
                  const memberInactive = linkedMember && linkedMember.membership_status !== 'member';
                  return (
                    <div className="form-group form-full">
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: memberInactive ? 'not-allowed' : 'pointer', opacity: memberInactive ? 0.5 : 1 }}>
                        <input type="checkbox" checked={!!editUser.active} onChange={setEdit('active')} disabled={memberInactive} />
                        Active
                      </label>
                      {memberInactive && (
                        <div style={{ fontSize: 11, color: '#dc2626', marginTop: 4 }}>
                          Cannot activate — linked member is <strong>{linkedMember.membership_status}</strong>. Change the member's status first.
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setEditUser(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleEdit} disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</button>
            </div>
          </div>
        </div>
      )}
      {/* Approve Modal */}
      {approveModal && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 640, maxHeight: '90vh' }}>
            <div className="modal-header">
              <div><h3>{t('usersRoles.approveAccount')}</h3><p>{approveModal.name} — {approveModal.email}</p></div>
              <button className="btn-icon" onClick={() => setApproveModal(null)}><FiX /></button>
            </div>
            <div className="modal-body">
              {/* Registration context banner */}
              {approveModal.registration_type === 'existing' && approveModal.requested_member_name && (
                <div style={{ background: '#eef2ff', border: '1px solid #a5b4fc', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 13, color: '#3730a3' }}>
                  <strong>{t('usersRoles.existingMember')}:</strong> {t('usersRoles.wantsToLink')} <strong>{approveModal.requested_member_name.replace(/\s+/g, ' ').trim()}</strong>
                  {approveModal.requested_member_card && <span style={{ fontFamily: 'monospace', marginLeft: 6 }}>({approveModal.requested_member_card})</span>}
                  <div style={{ fontSize: 12, marginTop: 4 }}>{t('common.phone')}: <strong>{approveModal.phone || '—'}</strong> · {t('usersRoles.phoneVerified')}</div>
                </div>
              )}
              {approveModal.registration_type === 'new' && (
                <div style={{ background: '#f3e8ff', border: '1px solid #d8b4fe', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 13, color: '#6b21a8' }}>
                  <strong>{t('usersRoles.newMember')}</strong> · {t('common.phone')}: <strong>{approveModal.phone || '—'}</strong>
                  <div style={{ fontSize: 12, marginTop: 4 }}>{t('usersRoles.willCreateMember')}</div>
                </div>
              )}

              {/* v1.9.90 — duplicate-detection warning. Backend attaches possible_matches
                  by matching name tokens / phone against existing members. Admin can
                  click "Link to this member" instead of creating another duplicate row. */}
              {approveModal.registration_type === 'new' && Array.isArray(approveModal.possible_matches) && approveModal.possible_matches.length > 0 && !approveForm.member_id && (
                <div style={{ background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 13, color: '#92400e' }}>
                  <strong>⚠ Possible duplicate — this person might already be a member:</strong>
                  <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {approveModal.possible_matches.map(m => (
                      <div key={m.id} style={{ background: '#fff', border: '1px solid #fde68a', borderRadius: 6, padding: '8px 10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                        <div style={{ fontSize: 12, color: '#374151' }}>
                          <div><strong>{m.full_name}</strong> <span style={{ fontFamily: 'monospace', color: '#6b7280', marginLeft: 4 }}>{m.card_number}</span></div>
                          <div style={{ color: '#6b7280', marginTop: 2 }}>
                            {m.phone || '—'} · <span style={{ textTransform: 'capitalize' }}>{m.membership_status || 'member'}</span>
                          </div>
                        </div>
                        <button
                          type="button"
                          className="btn btn-sm btn-primary"
                          onClick={() => setApproveForm(p => ({ ...p, member_id: m.id }))}
                        >
                          Link to this member
                        </button>
                      </div>
                    ))}
                  </div>
                  <div style={{ fontSize: 11, marginTop: 8, color: '#78350f' }}>
                    If none of these are the same person, just continue — a new member record will be created.
                  </div>
                </div>
              )}

              {/* Confirmation when admin picked a match */}
              {approveModal.registration_type === 'new' && approveForm.member_id && (
                <div style={{ background: '#dcfce7', border: '1px solid #86efac', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 13, color: '#166534', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>✓ Linking to existing member — no new record will be created.</span>
                  <button type="button" className="btn btn-sm btn-secondary" onClick={() => setApproveForm(p => ({ ...p, member_id: '' }))}>Undo</button>
                </div>
              )}

              <div className="form-group">
                <label>{t('usersRoles.assignRole')}</label>
                <select value={approveForm.role} onChange={e => setApproveForm(p => ({ ...p, role: e.target.value }))}>
                  <option value="member">{t('usersRoles.memberPortal')}</option>
                  <option value="secretary">Secretary</option>
                  <option value="finance">Finance</option>
                  <option value="cashier">Cashier</option>
                  <option value="pastor">Pastor</option>
                  <option value="admin">Admin</option>
                </select>
              </div>

              {/* For 'new' registration: let admin assign a spiritual father */}
              {approveForm.role === 'member' && approveModal.registration_type === 'new' && (
                <div className="form-group">
                  <label>{t('members.spiritualFather')} <span style={{ fontWeight: 400, color: '#9ca3af', fontSize: 11 }}>({t('common.optional')})</span></label>
                  <select value={approveForm.spiritual_father_id || ''}
                    onChange={e => setApproveForm(p => ({ ...p, spiritual_father_id: e.target.value }))}>
                    <option value="">— {t('members.unassigned')} —</option>
                    {members.filter(m => m.is_spiritual_father === 1).map(m => (
                      <option key={m.id} value={m.id}>{m.first_name} {m.middle_name || ''} {m.last_name}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* For other roles or 'existing' type: keep the manual link dropdown for override */}
              {approveForm.role === 'member' && approveModal.registration_type !== 'new' && (
                <div className="form-group">
                  <label>{t('usersRoles.linkToMember')}</label>
                  <MemberSearch members={members} linkedMemberIds={[]} value={approveForm.member_id} onChange={v => setApproveForm(p => ({ ...p, member_id: v }))} />
                  <small style={{ color: '#6b7280', marginTop: 4, display: 'block' }}>{t('usersRoles.linkToMemberHint')}</small>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setApproveModal(null)}>{t('common.cancel')}</button>
              <button className="btn btn-primary" onClick={async () => {
                try {
                  await approveUser(approveModal.id, approveForm);
                  showToast(`${approveModal.name} ${t('usersRoles.approvedSuccess')}`);
                  setApproveModal(null);
                  fetch();
                } catch (e) { showToast(e.response?.data?.error || t('myProfile.failed'), 'error'); }
              }}>
                ✓ {t('usersRoles.approveActivate')}
              </button>
            </div>
          </div>
        </div>
      )}

      <Toast message={toast?.msg} type={toast?.type} onClose={() => setToast(null)} />
    </div>
  );
};

export default UsersRoles;
